import React, { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import styled from "styled-components"
import * as variable from "../variables"

const PropertyMapSearchStyle = styled.div`
  background-color: #f5f5f5;
  padding: 40px 0;

  .map-container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
  }

  .map-header {
    text-align: center;
    margin-bottom: 30px;

    h2 {
      font-size: 36px;
      margin: 0 0 10px 0;
      color: #333;
    }

    p {
      font-size: 18px;
      color: #666;
      margin: 0;
    }
  }

  .filter-container {
    margin-bottom: 20px;
    max-width: 400px;

    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 600;
      color: #333;
    }

    select {
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 16px;
      line-height: 1.5;
      height: auto;
      background-color: white;
      cursor: pointer;
      -webkit-appearance: none;
      -moz-appearance: none;
      appearance: none;
      background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
      background-repeat: no-repeat;
      background-position: right 12px center;
      background-size: 16px;
      padding-right: 40px;

      &:focus {
        outline: none;
        border-color: ${variable.blue};
      }
    }
  }

  .map-layout {
    display: flex;
    flex-direction: row;
    gap: 20px;
    height: 600px;

    @media (max-width: ${variable.tabletWidth}) {
      flex-direction: column;
      height: auto;
    }
  }

  .property-list {
    width: 400px;
    height: 100%;
    overflow-y: auto;
    background-color: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    order: 1;

    @media (max-width: ${variable.tabletWidth}) {
      width: 100%;
      height: 400px;
      order: 2;
    }
  }

  .property-list-header {
    padding: 15px;
    border-bottom: 1px solid #eee;
    font-weight: 600;
    color: #333;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }

  .property-item {
    display: flex;
    padding: 15px;
    border-bottom: 1px solid #eee;
    text-decoration: none;
    transition: background-color 0.2s;
    cursor: pointer;

    &:hover,
    &.hovered {
      background-color: #fff8f8;
    }
  }

  .property-image {
    width: 100px;
    height: 75px;
    flex-shrink: 0;
    border-radius: 6px;
    overflow: hidden;
    background-color: #eee;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  .property-info {
    margin-left: 12px;
    flex: 1;
    min-width: 0;

    h4 {
      margin: 0 0 4px 0;
      font-size: 14px;
      font-weight: 600;
      color: #333;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .neighborhood {
      margin: 0 0 4px 0;
      font-size: 12px;
      color: ${variable.red};
    }

    .address {
      margin: 0;
      font-size: 11px;
      color: #666;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }

  .map-wrapper {
    flex: 1;
    min-height: 300px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    order: 2;

    @media (max-width: ${variable.tabletWidth}) {
      height: 400px;
      order: 1;
    }
  }

  .map-element {
    width: 100%;
    height: 100%;
    min-height: 300px;
    background-color: #e5e3df;
  }

  .map-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
  }

  .no-properties {
    padding: 40px 20px;
    text-align: center;
    color: #666;
  }
`

const SANITY_PROJECT_ID = "iqjqw8ps"
const SANITY_DATASET = "production"

const PropertyMapSearch = ({ section }) => {
  const data = useStaticQuery(graphql`
    query PropertyMapSearchQuery {
      allSanityProperty {
        nodes {
          headline
          tagline
          address
          slug {
            current
          }
          fields {
            geocodedLat
            geocodedLng
          }
          main_image {
            asset {
              _id
              url
            }
          }
          neighborhood {
            headline
            slug {
              current
            }
          }
        }
      }
      allSanityNeighborhood {
        nodes {
          headline
          slug {
            current
          }
        }
      }
    }
  `)

  const allProperties = useMemo(
    () =>
      data.allSanityProperty.nodes.map(property => ({
        ...property,
        coords:
          property.fields?.geocodedLat && property.fields?.geocodedLng
            ? {
                lat: property.fields.geocodedLat,
                lng: property.fields.geocodedLng,
              }
            : null,
      })),
    [data.allSanityProperty.nodes]
  )

  const neighborhoods = data.allSanityNeighborhood.nodes

  const [selectedNeighborhood, setSelectedNeighborhood] = useState("")
  const [filteredProperties, setFilteredProperties] = useState(allProperties)
  const [mapLoaded, setMapLoaded] = useState(false)
  const [hoveredPropertySlug, setHoveredPropertySlug] = useState(null)

  const mapRef = useRef(null)
  const mapInstanceRef = useRef(null)
  const markersRef = useRef([])
  const infoWindowRef = useRef(null)

  const {
    headline,
    subheadline,
    defaultZoom = 11,
    centerLat = 39.0997,
    centerLng = -94.5786,
    mapHeight = "600px",
    showFilters = true,
  } = section || {}

  // Build filter options
  const neighborhoodOptions = useMemo(
    () => [
      { value: "", label: "All Neighborhoods" },
      ...neighborhoods.map(n => ({
        value: n.slug?.current || "",
        label: n.headline,
      })),
    ],
    [neighborhoods]
  )

  // Filter properties based on selection
  useEffect(() => {
    if (!selectedNeighborhood) {
      setFilteredProperties(allProperties)
    } else {
      const filtered = allProperties.filter(
        p => p.neighborhood?.slug?.current === selectedNeighborhood
      )
      setFilteredProperties(filtered)
    }
  }, [selectedNeighborhood, allProperties])

  // Properties with valid coordinates
  const propertiesWithCoords = useMemo(
    () => filteredProperties.filter(p => p.coords),
    [filteredProperties]
  )

  // Load Google Maps script
  useEffect(() => {
    if (typeof window !== "undefined" && !window.google) {
      const apiKey = process.env.GATSBY_GOOGLE_MAPS_API_KEY
      if (!apiKey) {
        console.error("Google Maps API key is missing")
        return
      }
      const script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`
      script.async = true
      script.defer = true
      script.onload = () => setMapLoaded(true)
      document.head.appendChild(script)
    } else if (typeof window !== "undefined" && window.google) {
      setMapLoaded(true)
    }
  }, [])

  // Initialize map
  useEffect(() => {
    if (!mapLoaded || !mapRef.current || !window.google) return

    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat: centerLat, lng: centerLng },
      zoom: defaultZoom,
      styles: [
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "off" }],
        },
      ],
    })

    mapInstanceRef.current = map
    infoWindowRef.current = new window.google.maps.InfoWindow()
  }, [mapLoaded, centerLat, centerLng, defaultZoom])

  // Helper to build info window content
  const buildInfoWindowContent = useCallback(property => {
    const imageUrl = property.main_image?.asset?.url
    return `
      <div style="max-width: 240px;">
        ${
          imageUrl
            ? `<img src="${imageUrl}?w=240&h=140&fit=crop" alt="${property.headline}" style="width: 100%; height: 140px; object-fit: cover; border-radius: 8px 8px 0 0;" />`
            : ""
        }
        <div style="padding: 12px;">
          <h4 style="margin: 0 0 6px 0; font-size: 14px; font-weight: 600; color: #333;">${
            property.headline
          }</h4>
          <p style="margin: 0 0 10px 0; font-size: 12px; color: #666; line-height: 1.4;">${
            property.address || ""
          }</p>
          <a href="/property/${property.slug?.current}"
             style="display: inline-block; background: linear-gradient(to right, ${
               variable.blue
             }, ${
      variable.red
    }); color: white; padding: 8px 16px; border-radius: 20px; text-decoration: none; font-size: 12px; font-weight: 600;">
            View Property
          </a>
        </div>
      </div>
    `
  }, [])

  // Update markers when filtered properties change
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return

    // Clear existing markers
    markersRef.current.forEach(marker => marker.setMap(null))
    markersRef.current = []

    const bounds = new window.google.maps.LatLngBounds()

    propertiesWithCoords.forEach(property => {
      const marker = new window.google.maps.Marker({
        position: property.coords,
        map: mapInstanceRef.current,
        title: property.headline,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: variable.red,
          fillOpacity: 1,
          strokeColor: "#ffffff",
          strokeWeight: 2,
        },
      })

      bounds.extend(property.coords)

      marker.addListener("click", () => {
        infoWindowRef.current.setContent(buildInfoWindowContent(property))
        infoWindowRef.current.open(mapInstanceRef.current, marker)
      })

      marker.propertySlug = property.slug?.current
      markersRef.current.push(marker)
    })

    // Fit bounds if we have markers
    if (propertiesWithCoords.length > 0) {
      mapInstanceRef.current.fitBounds(bounds)
      const listener = window.google.maps.event.addListener(
        mapInstanceRef.current,
        "idle",
        () => {
          if (mapInstanceRef.current.getZoom() > 15) {
            mapInstanceRef.current.setZoom(15)
          }
          window.google.maps.event.removeListener(listener)
        }
      )
    }
  }, [propertiesWithCoords, mapLoaded, buildInfoWindowContent])

  // Pan to property on list hover
  const handlePropertyHover = useCallback(
    property => {
      setHoveredPropertySlug(property?.slug?.current || null)
      if (property?.coords && mapInstanceRef.current) {
        mapInstanceRef.current.panTo(property.coords)
        const marker = markersRef.current.find(
          m => m.propertySlug === property.slug?.current
        )
        if (marker && infoWindowRef.current) {
          infoWindowRef.current.setContent(buildInfoWindowContent(property))
          infoWindowRef.current.open(mapInstanceRef.current, marker)
        }
      }
    },
    [buildInfoWindowContent]
  )

  return (
    <PropertyMapSearchStyle>
      <section
        className="map-container"
        aria-label="Property locations map"
        itemScope
        itemType="https://schema.org/ItemList"
      >
        {(headline || subheadline) && (
          <header className="map-header">
            {headline && <h2 itemProp="name">{headline}</h2>}
            {subheadline && <p itemProp="description">{subheadline}</p>}
          </header>
        )}

        {showFilters && neighborhoods.length > 0 && (
          <div className="filter-container">
            <label htmlFor="neighborhood-filter">Filter by Neighborhood</label>
            <select
              id="neighborhood-filter"
              value={selectedNeighborhood}
              onChange={e => setSelectedNeighborhood(e.target.value)}
              aria-label="Select a neighborhood to filter properties"
            >
              {neighborhoodOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="map-layout" style={{ height: mapHeight }}>
          <nav
            className="property-list"
            aria-label="Property listings"
          >
            <div className="property-list-header" aria-live="polite">
              <span itemProp="numberOfItems">{propertiesWithCoords.length}</span> Properties
            </div>
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {propertiesWithCoords.map((property, index) => {
                const isHovered =
                  hoveredPropertySlug === property.slug?.current

                return (
                  <li
                    key={property.slug?.current}
                    itemScope
                    itemType="https://schema.org/Accommodation"
                    itemProp="itemListElement"
                  >
                    <meta itemProp="position" content={String(index + 1)} />
                    <Link
                      to={`/property/${property.slug?.current}`}
                      className={`property-item ${isHovered ? "hovered" : ""}`}
                      onMouseEnter={() => handlePropertyHover(property)}
                      onMouseLeave={() => handlePropertyHover(null)}
                      itemProp="url"
                    >
                      <div className="property-image">
                        {property.main_image?.asset?.url ? (
                          <img
                            src={`${property.main_image.asset.url}?w=100&h=75&fit=crop`}
                            alt={`${property.headline} - Corporate housing in Kansas City`}
                            itemProp="image"
                            loading="lazy"
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              backgroundColor: "#ddd",
                            }}
                            aria-hidden="true"
                          />
                        )}
                      </div>
                      <div className="property-info">
                        <h4 itemProp="name">{property.headline}</h4>
                        {property.neighborhood && (
                          <p className="neighborhood" itemProp="containedInPlace">
                            {property.neighborhood.headline}
                          </p>
                        )}
                        <p
                          className="address"
                          itemProp="address"
                          itemScope
                          itemType="https://schema.org/PostalAddress"
                        >
                          <span itemProp="streetAddress">{property.address}</span>
                        </p>
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
            {propertiesWithCoords.length === 0 && (
              <div className="no-properties" role="status">
                No properties with location data found.
              </div>
            )}
          </nav>

          <div
            className="map-wrapper"
            role="application"
            aria-label="Interactive map showing property locations in Kansas City"
          >
            <div ref={mapRef} className="map-element">
              {!mapLoaded && (
                <div className="map-loading" aria-live="polite">Loading map...</div>
              )}
            </div>
          </div>
        </div>
      </section>
    </PropertyMapSearchStyle>
  )
}

export default PropertyMapSearch
